import React from 'react';
import styles from './verifydata.module.scss';
import * as XLSX from 'xlsx';
import API_BASE_URL from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const VerifyData = ({
  verificationFormList,
  verificationLoading,
  verificationSearchTerm,
  verificationCurrentPage,
  verificationTotalPages,
  verificationItemsPerPage,
  handleVerificationSearch,
  handleVerificationPageChange,
  handleVerificationItemsPerPageChange,
  handleVerificationUpdate,
  verificationData,
  handleVerificationStatusChange,
  handleVerificationCommentChange,
  handleVerificationLocationChange,
  saveVerificationData,
  verificationFilters,
  showVerificationFilters,
  handleVerificationFilterChange,
  getFilteredVerificationRows,
  toggleVerificationFilters
}) => {
  const { makeAuthenticatedRequest } = useAuth();
  
  // Progress tracking state
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState('');
  const [spinningIcon, setSpinningIcon] = React.useState(1);
  
  // Debug logging
  console.log('VerifyData component - verificationData keys:', Object.keys(verificationData));
  console.log('VerifyData component - verificationFormList length:', verificationFormList?.length);
  console.log('VerifyData component - verificationData:', verificationData);
  console.log('VerifyData component - verificationFormList sample:', verificationFormList?.slice(0, 3));
  
  // Spinning icon effect during upload
  React.useEffect(() => {
    let interval;
    if (isUploading) {
      interval = setInterval(() => {
        setSpinningIcon(prev => prev === 4 ? 1 : prev + 1);
      }, 300); // Change icon every 300ms
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isUploading]);
  
  // Export to Excel function
  const exportToExcel = () => {
    try {
      // Create HTML table with embedded CSS styling
      const htmlContent = `
        <html>
          <head>
            <meta charset="utf-8">
            <title>सामानहरूको सत्यापन सारांश</title>
            <style>
              body {
                font-family: 'Noto Sans Devanagari', Arial, sans-serif;
                margin: 0;
                padding: 20px;
                background: white;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
                font-size: 11px;
              }
              th, td {
                border: 1px solid #000;
                padding: 6px;
                text-align: center;
                vertical-align: middle;
              }
              th {
                background-color: #2c3e50;
                color: white;
                font-weight: bold;
              }
              th.sub-header {
                background-color: #34495e;
                color: white;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f8f9fa;
              }
              tr:nth-child(odd) {
                background-color: #ffffff;
              }
              .status-approved {
                color: #28a745;
                font-weight: bold;
              }
              .status-pending {
                color: #ffc107;
                font-weight: bold;
              }
              .title-row {
                background-color: #f5f7fa;
                border: 2px solid #2c3e50;
                border-radius: 8px;
                padding: 10px;
                text-align: center;
                margin-bottom: 20px;
              }
              .title-row h1 {
                margin: 0;
                color: #2c3e50;
                font-size: 18px;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="title-row">
              <h1>सामानहरूको सत्यापन सारांश</h1>
              <p>Form 403 - Data Verification Report</p>
              <p>Generated on: ${new Date().toLocaleString('ne-NP')}</p>
              <p>Total Items: ${filteredRows.length}</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th rowspan="2">क्र.सं.</th>
                  <th rowspan="2">खरिद आदेश / हस्तान्तरण फारम नं.</th>
                  <th rowspan="2">जिन्सी वर्गीकरण संकेत नं.</th>
                  <th rowspan="2">जिन्सी खाता पाना नं.</th>
                  <th rowspan="2">सामानको नाम</th>
                  <th rowspan="2">स्पेसिफिकेशन</th>
                  <th rowspan="2">सामानको पहिचान नं.</th>
                  <th rowspan="2">मोडल नं.</th>
                  <th colspan="6">मूल्य (बिल बिजक अनुसार)</th>
                  <th rowspan="2">अन्य खर्च</th>
                  <th rowspan="2">अन्य खर्च समेत जम्मा रकम</th>
                  <th rowspan="2">कैफियत</th>
                  <th rowspan="2">स्थिति</th>
                  <th rowspan="2">टिप्पणी</th>
                  <th rowspan="2">स्थान</th>
                </tr>
                <tr>
                  <th class="sub-header">इकाई</th>
                  <th class="sub-header">परिमाण</th>
                  <th class="sub-header">दर</th>
                  <th class="sub-header">जम्मा मु.अ.कर बाहेक</th>
                  <th class="sub-header">मु.अ.कर</th>
                  <th class="sub-header">सामानको जम्मा मूल्य</th>
                </tr>
              </thead>
              <tbody>
                ${paginatedRows.map((form, index) => {
                  const status = verificationData[form.id]?.status === 'approved' ? 'स्वीकृत' : 'पेन्डिङ';
                  const statusClass = verificationData[form.id]?.status === 'approved' ? 'status-approved' : 'status-pending';
                  return `
                    <tr>
                      <td>${((verificationCurrentPage - 1) * verificationItemsPerPage) + index + 1}</td>
                      <td>${form.purchase_order_number || 'N/A'}</td>
                      <td>${form.classification_code || 'N/A'}</td>
                      <td>${form.ledger_page_number || 'N/A'}</td>
                      <td>${form.item_name || 'N/A'}</td>
                      <td>${form.specification || 'N/A'}</td>
                      <td>${form.item_identification_number || 'N/A'}</td>
                      <td>${form.model_number || 'N/A'}</td>
                      <td>${form.unit || 'N/A'}</td>
                      <td>${form.quantity || 'N/A'}</td>
                      <td>${form.rate || 'N/A'}</td>
                      <td>${form.total_excluding_vat || 'N/A'}</td>
                      <td>${form.vat || 'N/A'}</td>
                      <td>${form.total_item_value || 'N/A'}</td>
                      <td>${form.other_expenses || 'N/A'}</td>
                      <td>${form.total_with_other_expenses || 'N/A'}</td>
                      <td>${form.remarks || 'N/A'}</td>
                      <td class="${statusClass}">${status}</td>
                      <td>${verificationData[form.id]?.comment || ''}</td>
                      <td>${verificationData[form.id]?.location || ''}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filename = `Form403_Verification_Report_${dateStr}.xls`;
      
      link.download = filename;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Excel file exported successfully:', filename);
      alert('Excel file exported successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export current verification data function
  const exportCurrentData = () => {
    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Define headers for the template (excluding signature file fields)
      const headers = [
        'Item ID',
        'Form ID',
        // User specified order
        'कार्यालय',
        'कार्यालय ठेगाना',
        'कार्यालय कोड नं.',
        'दाखिला मिति',
        'दाखिला प्रतिवेदन नं.',
        'क्र.सं.',
        'खरिद आदेश / हस्तान्तरण फारम नं.',
        'जिन्सी वर्गीकरण संकेत नं.',
        'जिन्सी खाता पाना नं.',
        'सामानको नाम',
        'स्पेसिफिकेशन',
        'सामानको पहिचान नं.',
        'मोडल नं.',
        'इकाई',
        'परिमाण',
        'दर',
        'जम्मा मु.अ.कर बाहेक',
        'मु.अ.कर',
        'सामानको जम्मा मूल्य',
        'अन्य खर्च',
        'अन्य खर्च समेत जम्मा रकम',
        'कैफियत',
        'फाँटवालाको नाम',
        'फाँटवालाको पद',
        'फाँटवालाको मिति',
        'शाखा प्रमुखको नाम',
        'शाखा प्रमुखको पद',
        'शाखा प्रमुखको मिति',
        'कार्यालय प्रमुखको नाम',
        'कार्यालय प्रमुखको पद',
        'कार्यालय प्रमुखको मिति',
        'फर्मको नाम',
        'फर्मको दस्तखत',
        'फर्मको मिति',
        // Verification fields
        'स्थिति',
        'टिप्पणी',
        'स्थान'
      ];
      
      // Get current verification data
      const currentData = [];
      verificationFormList.forEach(item => {
        const verification = verificationData[item.id];
        const form403 = item.form403; // Get the Form403 object
        
        currentData.push([
          item.id, // Item ID
          form403?.id || '', // Form ID
          // User specified order
          item.office_name || '', // कार्यालय
          item.office_address || '', // कार्यालय ठेगाना
          item.office_code || '', // कार्यालय कोड नं.
          item.admission_date || '', // दाखिला मिति
          item.admission_report_number || '', // दाखिला प्रतिवेदन नं.
          item.serial_number || '', // क्र.सं.
          item.purchase_order_number || '', // खरिद आदेश / हस्तान्तरण फारम नं.
          item.classification_code || '', // जिन्सी वर्गीकरण संकेत नं.
          item.ledger_page_number || '', // जिन्सी खाता पाना नं.
          item.item_name || '', // सामानको नाम
          item.specification || '', // स्पेसिफिकेशन
          item.item_identification_number || '', // सामानको पहिचान नं.
          item.model_number || '', // मोडल नं.
          item.unit || '', // इकाई
          item.quantity || '', // परिमाण
          item.rate || '', // दर
          item.total_excluding_vat || '', // जम्मा मु.अ.कर बाहेक
          item.vat || '', // मु.अ.कर
          item.total_item_value || '', // सामानको जम्मा मूल्य
          item.other_expenses || '', // अन्य खर्च
          item.total_with_other_expenses || '', // अन्य खर्च समेत जम्मा रकम
          item.remarks || '', // कैफियत
          form403?.fontwal_name || '', // फाँटवालाको नाम
          form403?.fontwal_designation || '', // फाँटवालाको पद
          form403?.fontwal_date || '', // फाँटवालाको मिति
          form403?.branch_head_name || '', // शाखा प्रमुखको नाम
          form403?.branch_head_designation || '', // शाखा प्रमुखको पद
          form403?.branch_head_date || '', // शाखा प्रमुखको मिति
          form403?.office_head_name || '', // कार्यालय प्रमुखको नाम
          form403?.office_head_designation || '', // कार्यालय प्रमुखको पद
          form403?.office_head_date || '', // कार्यालय प्रमुखको मिति
          form403?.firm_name || '', // फर्मको नाम
          form403?.firm_date || '', // फर्मको मिति
          // Verification fields
          verification?.status || 'pending', // स्थिति
          verification?.comment || '', // टिप्पणी
          verification?.location || '' // स्थान
        ]);
      });
      
      // Create worksheet with headers and current data
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...currentData]);
      
      // Set column widths (adjusted for ALL Form403 and Form403Item fields)
      const columnWidths = [
        { wch: 15 }, // Item ID
        { wch: 12 }, // Form ID
        // User specified order
        { wch: 25 }, // कार्यालय
        { wch: 30 }, // कार्यालय ठेगाना
        { wch: 20 }, // कार्यालय कोड नं.
        { wch: 15 }, // दाखिला मिति
        { wch: 25 }, // दाखिला प्रतिवेदन नं.
        { wch: 10 }, // क्र.सं.
        { wch: 25 }, // खरिद आदेश / हस्तान्तरण फारम नं.
        { wch: 22 }, // जिन्सी वर्गीकरण संकेत नं.
        { wch: 20 }, // जिन्सी खाता पाना नं.
        { wch: 30 }, // सामानको नाम
        { wch: 25 }, // स्पेसिफिकेशन
        { wch: 28 }, // सामानको पहिचान नं.
        { wch: 18 }, // मोडल नं.
        { wch: 12 }, // इकाई
        { wch: 12 }, // परिमाण
        { wch: 15 }, // दर
        { wch: 25 }, // जम्मा मु.अ.कर बाहेक
        { wch: 15 }, // मु.अ.कर
        { wch: 25 }, // सामानको जम्मा मूल्य
        { wch: 20 }, // अन्य खर्च
        { wch: 30 }, // अन्य खर्च समेत जम्मा रकम
        { wch: 25 }, // कैफियत
        { wch: 25 }, // फाँटवालाको नाम
        { wch: 20 }, // फाँटवालाको पद
        { wch: 15 }, // फाँटवालाको मिति
        { wch: 25 }, // शाखा प्रमुखको नाम
        { wch: 20 }, // शाखा प्रमुखको पद
        { wch: 15 }, // शाखा प्रमुखको मिति
        { wch: 25 }, // कार्यालय प्रमुखको नाम
        { wch: 20 }, // कार्यालय प्रमुखको पद
        { wch: 15 }, // कार्यालय प्रमुखको मिति
                 { wch: 25 }, // फर्मको नाम
         { wch: 15 }, // फर्मको दस्तखत
         { wch: 15 }, // फर्मको मिति
        // Verification fields
        { wch: 25 }, // स्थिति
        { wch: 30 }, // टिप्पणी
        { wch: 25 }  // स्थान
      ];
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Current Verification Data');
      
      // Generate filename
      const date = new Date();
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const filename = `Form403_Current_Verification_Data_${dateStr}.xlsx`;
      
      // Write and download file
      XLSX.writeFile(workbook, filename);
      
      console.log('Current data exported successfully:', filename);
      alert('Current verification data exported successfully!');
    } catch (error) {
      console.error('Error exporting current data:', error);
      alert('Error exporting current data. Please try again.');
    }
  };

  // Download template function
  const downloadTemplate = () => {
    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Define headers for the template (excluding signature file fields)
      const headers = [
        'Item ID',
        'Form ID',
        // User specified exact order
        'कार्यालय',
        'कार्यालय ठेगाना',
        'कार्यालय कोड नं.',
        'दाखिला मिति',
        'दाखिला प्रतिवेदन नं.',
        'क्र.सं.',
        'खरिद आदेश / हस्तान्तरण फारम नं.',
        'जिन्सी वर्गीकरण संकेत नं.',
        'जिन्सी खाता पाना नं.',
        'सामानको नाम',
        'स्पेसिफिकेशन',
        'सामानको पहिचान नं.',
        'मोडल नं.',
        'इकाई',
        'परिमाण',
        'दर',
        'जम्मा मु.अ.कर बाहेक',
        'मु.अ.कर',
        'सामानको जम्मा मूल्य',
        'अन्य खर्च',
        'अन्य खर्च समेत जम्मा रकम',
        'कैफियत',
        'फाँटवालाको नाम',
        'फाँटवालाको पद',
        'फाँटवालाको मिति',
        'शाखा प्रमुखको नाम',
        'शाखा प्रमुखको पद',
        'शाखा प्रमुखको मिति',
        'कार्यालय प्रमुखको नाम',
        'कार्यालय प्रमुखको पद',
        'कार्यालय प्रमुखको मिति',
        'फर्मको नाम',
        'फर्मको मिति',
        // Verification fields
        'स्थिति',
        'टिप्पणी',
        'स्थान'
      ];
      
      // Create sample data rows with 2 dummy data entries
      const sampleData1 = [
        '1', // Item ID
        '1', // Form ID
        // User specified exact order
        'मुख्य कार्यालय', // कार्यालय
        'काठमाडौं, नेपाल', // कार्यालय ठेगाना
        'OFF001', // कार्यालय कोड नं.
        '2024-01-15', // दाखिला मिति
        'RPT001', // दाखिला प्रतिवेदन नं.
        '001', // क्र.सं.
        'PO001', // खरिद आदेश / हस्तान्तरण फारम नं.
        'CC001', // जिन्सी वर्गीकरण संकेत नं.
        'LP001', // जिन्सी खाता पाना नं.
        'कम्प्युटर', // सामानको नाम
        'Dell Latitude 5520, 16GB RAM, 512GB SSD', // स्पेसिफिकेशन
        'ITM001', // सामानको पहिचान नं.
        'DL5520', // मोडल नं.
        'Unit', // इकाई
        '5', // परिमाण
        '85000', // दर
        '425000', // जम्मा मु.अ.कर बाहेक
        '76500', // मु.अ.कर
        '501500', // सामानको जम्मा मूल्य
        '5000', // अन्य खर्च
        '506500', // अन्य खर्च समेत जम्मा रकम
        'IT equipment for office use', // कैफियत
        'राम बहादुर श्रेष्ठ', // फाँटवालाको नाम
        'फाँटवाला', // फाँटवालाको पद
        '2024-01-15', // फाँटवालाको मिति
        'सुरेश कुमार श्रेष्ठ', // शाखा प्रमुखको नाम
        'शाखा प्रमुख', // शाखा प्रमुखको पद
        '2024-01-16', // शाखा प्रमुखको मिति
        'महेश कुमार श्रेष्ठ', // कार्यालय प्रमुखको नाम
        'कार्यालय प्रमुख', // कार्यालय प्रमुखको पद
        '2024-01-17', // कार्यालय प्रमुखको मिति
        'ABC Company Pvt. Ltd.', // फर्मको नाम
        '2024-01-18', // फर्मको मिति
        // Verification fields
        'pending', // स्थिति (default)
        'Under review', // टिप्पणी
        'Store Room A'  // स्थान
      ];

      const sampleData2 = [
        '2', // Item ID
        '2', // Form ID
        // User specified exact order
        'शाखा कार्यालय', // कार्यालय
        'पोखरा, नेपाल', // कार्यालय ठेगाना
        'OFF002', // कार्यालय कोड नं.
        '2024-01-20', // दाखिला मिति
        'RPT002', // दाखिला प्रतिवेदन नं.
        '002', // क्र.सं.
        'PO002', // खरिद आदेश / हस्तान्तरण फारम नं.
        'CC002', // जिन्सी वर्गीकरण संकेत नं.
        'LP002', // जिन्सी खाता पाना नं.
        'प्रिन्टर', // सामानको नाम
        'HP LaserJet Pro M404n, Monochrome Laser', // स्पेसिफिकेशन
        'ITM002', // सामानको पहिचान नं.
        'HPLJ404', // मोडल नं.
        'Unit', // इकाई
        '3', // परिमाण
        '45000', // दर
        '135000', // जम्मा मु.अ.कर बाहेक
        '24300', // मु.अ.कर
        '159300', // सामानको जम्मा मूल्य
        '3000', // अन्य खर्च
        '162300', // अन्य खर्च समेत जम्मा रकम
        'Printing equipment for branch office', // कैफियत
        'सुनिल कुमार श्रेष्ठ', // फाँटवालाको नाम
        'फाँटवाला', // फाँटवालाको पद
        '2024-01-20', // फाँटवालाको मिति
        'राजेश कुमार श्रेष्ठ', // शाखा प्रमुखको नाम
        'शाखा प्रमुख', // शाखा प्रमुखको पद
        '2024-01-21', // शाखा प्रमुखको मिति
        'दीपक कुमार श्रेष्ठ', // कार्यालय प्रमुखको नाम
        'कार्यालय प्रमुख', // कार्यालय प्रमुखको पद
        '2024-01-22', // कार्यालय प्रमुखको मिति
        'XYZ Company Pvt. Ltd.', // फर्मको नाम
        '2024-01-23', // फर्मको मिति
        // Verification fields
        'pending', // स्थिति (default)
        'Pending verification', // टिप्पणी
        'Store Room B'  // स्थान
      ];
      
      // Create worksheet with headers and 2 sample data rows
      const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleData1, sampleData2]);
      
      // Set column widths (adjusted for ALL Form403 and Form403Item fields)
      const columnWidths = [
        { wch: 10 }, // Item ID
        { wch: 12 }, // Form ID
        // User specified exact order
        { wch: 25 }, // कार्यालय
        { wch: 30 }, // कार्यालय ठेगाना
        { wch: 20 }, // कार्यालय कोड नं.
        { wch: 15 }, // दाखिला मिति
        { wch: 25 }, // दाखिला प्रतिवेदन नं.
        { wch: 10 }, // क्र.सं.
        { wch: 25 }, // खरिद आदेश / हस्तान्तरण फारम नं.
        { wch: 22 }, // जिन्सी वर्गीकरण संकेत नं.
        { wch: 20 }, // जिन्सी खाता पाना नं.
        { wch: 30 }, // सामानको नाम
        { wch: 25 }, // स्पेसिफिकेशन
        { wch: 28 }, // सामानको पहिचान नं.
        { wch: 18 }, // मोडल नं.
        { wch: 12 }, // इकाई
        { wch: 12 }, // परिमाण
        { wch: 15 }, // दर
        { wch: 25 }, // जम्मा मु.अ.कर बाहेक
        { wch: 15 }, // मु.अ.कर
        { wch: 25 }, // सामानको जम्मा मूल्य
        { wch: 20 }, // अन्य खर्च
        { wch: 30 }, // अन्य खर्च समेत जम्मा रकम
        { wch: 25 }, // कैफियत
        { wch: 25 }, // फाँटवालाको नाम
        { wch: 20 }, // फाँटवालाको पद
        { wch: 15 }, // फाँटवालाको मिति
        { wch: 25 }, // शाखा प्रमुखको नाम
        { wch: 20 }, // शाखा प्रमुखको पद
        { wch: 15 }, // शाखा प्रमुखको मिति
        { wch: 25 }, // कार्यालय प्रमुखको नाम
        { wch: 20 }, // कार्यालय प्रमुखको पद
        { wch: 15 }, // कार्यालय प्रमुखको मिति
        { wch: 25 }, // फर्मको नाम
        { wch: 15 }, // फर्मको मिति
        // Verification fields
        { wch: 25 }, // स्थिति
        { wch: 30 }, // टिप्पणी
        { wch: 25 }  // स्थान
      ];
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Verification Template');
      
      // Generate filename
      const date = new Date();
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const filename = `Form403_Verification_Template_${dateStr}.xlsx`;
      
      // Write and download file
      XLSX.writeFile(workbook, filename);
      
      console.log('Template downloaded successfully:', filename);
      alert('Template downloaded successfully!\n\nNote: When uploading new data, it will be added at the top of the data list in the exact order they appear in your Excel file. Row 1 will appear first, Row 2 second, and so on.');
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template. Please try again.');
    }
  };

  // Get existing Form403 data from database
  const getExistingForm403Data = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/form403-items/`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch existing data');
      }

      const data = await response.json();
      console.log('Existing Form403 data:', data);

      // Transform existing data to match Excel format
      const existingData = data.results || data;
      return existingData.map((item, index) => ({
        item_id: item.id || (index + 1),
        form_id: item.form403 || (index + 1),
        office_name: item.office_name || '',
        office_address: item.office_address || '',
        office_code: item.office_code || '',
        admission_date: item.admission_date || '',
        admission_report_number: item.admission_report_number || '',
        serial_number: item.serial_number || '',
        purchase_order_number: item.purchase_order_number || '',
        classification_code: item.classification_code || '',
        ledger_page_number: item.ledger_page_number || '',
        item_name: item.item_name || '',
        specification: item.specification || '',
        item_identification_number: item.item_identification_number || '',
        model_number: item.model_number || '',
        unit: item.unit || '',
        quantity: item.quantity || '',
        rate: item.rate || '',
        total_excluding_vat: item.total_excluding_vat || '',
        vat: item.vat || '',
        total_item_value: item.total_item_value || '',
        other_expenses: item.other_expenses || '',
        total_with_other_expenses: item.total_with_other_expenses || '',
        remarks: item.remarks || '',
        fontwal_name: item.fontwal_name || '',
        fontwal_designation: item.fontwal_designation || '',
        fontwal_date: item.fontwal_date || '',
        branch_head_name: item.branch_head_name || '',
        branch_head_designation: item.branch_head_designation || '',
        branch_head_date: item.branch_head_date || '',
        office_head_name: item.office_head_name || '',
        office_head_designation: item.office_head_designation || '',
        office_head_date: item.office_head_date || '',
        firm_name: item.firm_name || '',
        firm_date: item.firm_date || '',
        status: 'pending',
        comment: '',
        location: ''
      }));
    } catch (error) {
      console.error('Error fetching existing data:', error);
      return [];
    }
  };

  // Create updated Excel file with combined data
  const createUpdatedExcelFile = async (combinedData) => {
    try {
      // Define headers for the template (same as download template)
      const headers = [
        'Item ID',
        'Form ID',
        'कार्यालय',
        'कार्यालय ठेगाना',
        'कार्यालय कोड नं.',
        'दाखिला मिति',
        'दाखिला प्रतिवेदन नं.',
        'क्र.सं.',
        'खरिद आदेश / हस्तान्तरण फारम नं.',
        'जिन्सी वर्गीकरण संकेत नं.',
        'जिन्सी खाता पाना नं.',
        'सामानको नाम',
        'स्पेसिफिकेशन',
        'सामानको पहिचान नं.',
        'मोडल नं.',
        'इकाई',
        'परिमाण',
        'दर',
        'जम्मा मु.अ.कर बाहेक',
        'मु.अ.कर',
        'सामानको जम्मा मूल्य',
        'अन्य खर्च',
        'अन्य खर्च समेत जम्मा रकम',
        'कैफियत',
        'फाँटवालाको नाम',
        'फाँटवालाको पद',
        'फाँटवालाको मिति',
        'शाखा प्रमुखको नाम',
        'शाखा प्रमुखको पद',
        'शाखा प्रमुखको मिति',
        'कार्यालय प्रमुखको नाम',
        'कार्यालय प्रमुखको पद',
        'कार्यालय प्रमुखको मिति',
        'फर्मको नाम',
        'फर्मको मिति',
        'स्थिति',
        'टिप्पणी',
        'स्थान'
      ];

      // Convert combined data to rows
      const rows = combinedData.map(item => [
        item.item_id,
        item.form_id,
        item.office_name,
        item.office_address,
        item.office_code,
        item.admission_date,
        item.admission_report_number,
        item.serial_number,
        item.purchase_order_number,
        item.classification_code,
        item.ledger_page_number,
        item.item_name,
        item.specification,
        item.item_identification_number,
        item.model_number,
        item.unit,
        item.quantity,
        item.rate,
        item.total_excluding_vat,
        item.vat,
        item.total_item_value,
        item.other_expenses,
        item.total_with_other_expenses,
        item.remarks,
        item.fontwal_name,
        item.fontwal_designation,
        item.fontwal_date,
        item.branch_head_name,
        item.branch_head_designation,
        item.branch_head_date,
        item.office_head_name,
        item.office_head_designation,
        item.office_head_date,
        item.firm_name,
        item.firm_date,
        item.status,
        item.comment,
        item.location
      ]);

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Set column widths
      const columnWidths = [
        { wch: 10 }, // Item ID
        { wch: 12 }, // Form ID
        { wch: 25 }, // कार्यालय
        { wch: 30 }, // कार्यालय ठेगाना
        { wch: 20 }, // कार्यालय कोड नं.
        { wch: 15 }, // दाखिला मिति
        { wch: 25 }, // दाखिला प्रतिवेदन नं.
        { wch: 10 }, // क्र.सं.
        { wch: 25 }, // खरिद आदेश / हस्तान्तरण फारम नं.
        { wch: 22 }, // जिन्सी वर्गीकरण संकेत नं.
        { wch: 20 }, // जिन्सी खाता पाना नं.
        { wch: 30 }, // सामानको नाम
        { wch: 25 }, // स्पेसिफिकेशन
        { wch: 28 }, // सामानको पहिचान नं.
        { wch: 18 }, // मोडल नं.
        { wch: 12 }, // इकाई
        { wch: 12 }, // परिमाण
        { wch: 15 }, // दर
        { wch: 25 }, // जम्मा मु.अ.कर बाहेक
        { wch: 15 }, // मु.अ.कर
        { wch: 25 }, // सामानको जम्मा मूल्य
        { wch: 20 }, // अन्य खर्च
        { wch: 30 }, // अन्य खर्च समेत जम्मा रकम
        { wch: 25 }, // कैफियत
        { wch: 25 }, // फाँटवालाको नाम
        { wch: 20 }, // फाँटवालाको पद
        { wch: 15 }, // फाँटवालाको मिति
        { wch: 25 }, // शाखा प्रमुखको नाम
        { wch: 20 }, // शाखा प्रमुखको पद
        { wch: 15 }, // शाखा प्रमुखको मिति
        { wch: 25 }, // कार्यालय प्रमुखको नाम
        { wch: 20 }, // कार्यालय प्रमुखको पद
        { wch: 15 }, // कार्यालय प्रमुखको मिति
        { wch: 25 }, // फर्मको नाम
        { wch: 15 }, // फर्मको मिति
        { wch: 25 }, // स्थिति
        { wch: 30 }, // टिप्पणी
        { wch: 25 }  // स्थान
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Updated Data');

      // Generate filename
      const date = new Date();
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const filename = `Form403_Updated_Data_${dateStr}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      console.log('Updated Excel file created successfully:', filename);
      alert(`Updated Excel file created: ${filename}\nNew data has been added at the top of the file in the exact order they appeared in your Excel file.`);
    } catch (error) {
      console.error('Error creating updated Excel file:', error);
      alert('Error creating updated Excel file. Please try again.');
    }
  };

  // Handle Excel upload function
  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      console.log('Starting Excel file upload:', file.name);
      const data = await readExcelFile(file);
      await processBulkVerificationData(data);
      event.target.value = ''; // Clear the input
    } catch (error) {
      console.error('Error processing Excel file:', error);
      
      // Provide more detailed error message to help user
      let errorMessage = 'Error processing Excel file.\n\n';
      errorMessage += 'Please ensure:\n';
      errorMessage += '1. You are using the downloaded template format\n';
      errorMessage += '2. The first row contains the correct headers\n';
      errorMessage += '3. The file is not corrupted\n';
      errorMessage += '4. The file is in .xlsx format\n\n';
      errorMessage += `Error details: ${error.message}`;
      
      alert(errorMessage);
      event.target.value = ''; // Clear the input
    }
  };

  // Read Excel file function
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          console.log('Reading Excel file:', file.name, 'Size:', file.size, 'Type:', file.type);
          
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          console.log('Workbook sheets:', workbook.SheetNames);
          
          if (workbook.SheetNames.length === 0) {
            throw new Error('No sheets found in the Excel file.');
          }
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          if (!worksheet) {
            throw new Error(`Sheet "${sheetName}" not found in the Excel file.`);
          }
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          console.log('Raw JSON data length:', jsonData.length);
          console.log('First few rows of raw data:', jsonData.slice(0, 3));
          
          if (jsonData.length === 0) {
            throw new Error('No data found in the Excel file.');
          }
          
          // Remove header row and get data
          const headers = jsonData[0];
          const rows = jsonData.slice(1);
          
          console.log('Headers found:', headers);
          console.log('Headers count:', headers.length);
          console.log('Rows count:', rows.length);
          
          // Validate that headers exist and are not empty
          if (!headers || headers.length === 0) {
            throw new Error('No headers found in the Excel file. Please ensure the first row contains column headers.');
          }
          
          // Check for empty or undefined headers
          const emptyHeaders = headers.map((header, index) => ({ header, index })).filter(item => !item.header);
          if (emptyHeaders.length > 0) {
            console.warn('Empty headers found at positions:', emptyHeaders.map(item => item.index + 1));
          }
          
          resolve({ headers, rows });
        } catch (error) {
          console.error('Error reading Excel file:', error);
          reject(new Error(`Error reading Excel file: ${error.message}`));
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(new Error(`File reading error: ${error.message}`));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Process bulk verification data
  const processBulkVerificationData = async (data) => {
    try {
      const { headers, rows } = data;
      
      console.log('Processing Excel data:', { headers, rowsCount: rows.length });
      
      // Validate headers (excluding signature file fields)
      const expectedHeaders = [
        'Item ID',
        'Form ID',
        // User specified exact order
        'कार्यालय',
        'कार्यालय ठेगाना',
        'कार्यालय कोड नं.',
        'दाखिला मिति',
        'दाखिला प्रतिवेदन नं.',
        'क्र.सं.',
        'खरिद आदेश / हस्तान्तरण फारम नं.',
        'जिन्सी वर्गीकरण संकेत नं.',
        'जिन्सी खाता पाना नं.',
        'सामानको नाम',
        'स्पेसिफिकेशन',
        'सामानको पहिचान नं.',
        'मोडल नं.',
        'इकाई',
        'परिमाण',
        'दर',
        'जम्मा मु.अ.कर बाहेक',
        'मु.अ.कर',
        'सामानको जम्मा मूल्य',
        'अन्य खर्च',
        'अन्य खर्च समेत जम्मा रकम',
        'कैफियत',
        'फाँटवालाको नाम',
        'फाँटवालाको पद',
        'फाँटवालाको मिति',
        'शाखा प्रमुखको नाम',
        'शाखा प्रमुखको पद',
        'शाखा प्रमुखको मिति',
        'कार्यालय प्रमुखको नाम',
        'कार्यालय प्रमुखको पद',
        'कार्यालय प्रमुखको मिति',
        'फर्मको नाम',
        'फर्मको मिति',
        // Verification fields
        'स्थिति',
        'टिप्पणी',
        'स्थान'
      ];
    
    // Check if headers match expected format (allow for extra columns)
    const isValidHeaders = expectedHeaders.every((header, index) => 
      headers[index] === header
    ) && headers.length >= expectedHeaders.length;
    
    console.log('Header validation:', { 
      expectedHeaders, 
      actualHeaders: headers, 
      isValidHeaders 
    });
    
    if (!isValidHeaders) {
      console.error('Header validation failed:', {
        expected: expectedHeaders,
        actual: headers,
        differences: expectedHeaders.filter((header, index) => headers[index] !== header)
      });
      
      // Provide more detailed error information
      const missingHeaders = [];
      const wrongHeaders = [];
      
      expectedHeaders.forEach((header, index) => {
        if (headers[index] !== header) {
          if (headers[index] === undefined || headers[index] === null) {
            missingHeaders.push(`Missing header at position ${index + 1}: "${header}"`);
          } else {
            wrongHeaders.push(`Wrong header at position ${index + 1}: Expected "${header}", Got "${headers[index]}"`);
          }
        }
      });
      
      let errorMessage = 'Invalid template format. Please use the downloaded template.\n\n';
      if (missingHeaders.length > 0) {
        errorMessage += 'Missing headers:\n' + missingHeaders.join('\n') + '\n\n';
      }
      if (wrongHeaders.length > 0) {
        errorMessage += 'Wrong headers:\n' + wrongHeaders.join('\n') + '\n\n';
      }
      errorMessage += `Expected ${expectedHeaders.length} headers, but found ${headers.length} headers.`;
      
      throw new Error(errorMessage);
    }
    
    // Process each row for bulk create/update - maintain exact Excel order
    const processedData = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      console.log(`Processing row ${i}:`, row);
      console.log(`Row length: ${row.length}, Status value: ${row[35]} (type: ${typeof row[35]})`);
      
      if (row.length === 0 || row.every(cell => !cell)) {
        console.log(`Skipping empty row ${i}`);
        continue; // Skip empty rows
      }
      
      try {
        // Map data according to the exact order (adjusted for Item ID and Form ID columns)
        const formData = {
          // Item ID and Form ID (for reference only, not used in API calls)
          item_id: row[0] || '',
          form_id: row[1] || '',
          
          // Form403Item fields (first part)
          office_name: row[2] || '',
          office_address: row[3] || '',
          office_code: row[4] || '',
          admission_date: row[5] || '',
          admission_report_number: row[6] || '',
          serial_number: row[7] || '',
          purchase_order_number: row[8] || '',
          classification_code: row[9] || '',
          ledger_page_number: row[10] || '',
          item_name: row[11] || '',
          specification: row[12] || '',
          item_identification_number: row[13] || '',
          model_number: row[14] || '',
          unit: row[15] || '',
          quantity: row[16] || '',
          rate: row[17] || '',
          total_excluding_vat: row[18] || '',
          vat: row[19] || '',
          total_item_value: row[20] || '',
          other_expenses: row[21] || '',
          total_with_other_expenses: row[22] || '',
          remarks: row[23] || '',
          
          // Form403 fields (signature and firm info)
          fontwal_name: row[24] || '',
          fontwal_designation: row[25] || '',
          fontwal_date: row[26] || '',
          branch_head_name: row[27] || '',
          branch_head_designation: row[28] || '',
          branch_head_date: row[29] || '',
          office_head_name: row[30] || '',
          office_head_designation: row[31] || '',
          office_head_date: row[32] || '',
          firm_name: row[33] || '',
          firm_date: row[34] || '',
          
          // Verification fields - handle different data types safely
          status: (() => {
            const statusValue = row[35];
            if (statusValue && typeof statusValue === 'string') {
              return statusValue.toLowerCase() === 'approved' ? 'approved' : 'pending';
            } else if (statusValue && typeof statusValue === 'number') {
              // If it's a number, treat as pending
              return 'pending';
            } else {
              return 'pending';
            }
          })(),
          comment: row[36] || '',
          location: row[37] || '',
          
          // Add upload order information to maintain sequence
          upload_order: i,
          upload_timestamp: new Date().toISOString()
        };
      
        console.log(`Mapped data for row ${i}:`, formData);
        processedData.push(formData);
      } catch (error) {
        console.error(`Error processing row ${i}:`, error, row);
        throw new Error(`Error processing row ${i}: ${error.message}`);
      }
    }
    
    if (processedData.length === 0) {
      throw new Error('No valid data found in the Excel file.');
    }
    
    // Sort processed data by upload order to ensure exact Excel order is maintained
    processedData.sort((a, b) => a.upload_order - b.upload_order);
    
    console.log('Final processed data order:', processedData.map((item, index) => ({
      index,
      upload_order: item.upload_order,
      item_name: item.item_name
    })));
    
    // Get existing data from database to maintain order
    const existingData = await getExistingForm403Data();
    
    // Combine new data at the top with existing data
    const combinedData = [...processedData, ...existingData];
    
    // Create updated Excel file with combined data
    await createUpdatedExcelFile(combinedData);
    
    // Confirm with user
    const confirmed = window.confirm(
      `Found ${processedData.length} new items to process. These will be added at the top of the data file in the exact order they appear in your Excel file. Each row will create a new "दाखिला प्रतिवेदन फाराम मुख्य फाराम" (Admission Report Form - Main Form) with its associated item. Do you want to proceed with bulk create?`
    );
    
    if (confirmed) {
      await bulkCreateUpdateData(processedData);
    }
    } catch (error) {
      console.error('Error in processBulkVerificationData:', error);
      throw new Error(`Error processing Excel file: ${error.message}`);
    }
  };

  // Bulk create data for Form403 and Form403Item
  const bulkCreateUpdateData = async (data) => {
    try {
      // Initialize upload progress
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('Starting upload process...');
      
      const results = [];
      
      // Process data in the exact order it was uploaded (maintain Excel row order)
      for (let i = 0; i < data.length; i++) {
        const itemData = data[i];
        console.log(`Processing upload item ${i + 1}/${data.length}: ${itemData.item_name || 'Unknown item'}`);
        
        // Update progress
        const progress = Math.round(((i + 1) / data.length) * 100);
        setUploadProgress(progress);
        setUploadStatus(`Processing item ${i + 1} of ${data.length}: ${itemData.item_name || 'Unknown item'}`);
        
        try {
          // Prepare Form403Item data for the context
          const itemDataForContext = {
            // Form403Item fields
            serial_number: itemData.serial_number,
            purchase_order_number: itemData.purchase_order_number,
            classification_code: itemData.classification_code,
            ledger_page_number: itemData.ledger_page_number,
            item_name: itemData.item_name,
            specification: itemData.specification,
            item_identification_number: itemData.item_identification_number,
            model_number: itemData.model_number,
            unit: itemData.unit,
            quantity: itemData.quantity,
            rate: itemData.rate,
            total_excluding_vat: itemData.total_excluding_vat,
            vat: itemData.vat,
            total_item_value: itemData.total_item_value,
            other_expenses: itemData.other_expenses,
            total_with_other_expenses: itemData.total_with_other_expenses,
            remarks: itemData.remarks
          };

          // Create new Form403 record with items_data in context
          const requestBody = {
            // Form403 main form fields
            office_name: itemData.office_name || '',
            office_address: itemData.office_address || '',
            office_code: itemData.office_code || '',
            admission_date: itemData.admission_date || null,
            admission_report_number: itemData.admission_report_number || '',
            
            // Signature information (names and dates)
            fontwal_name: itemData.fontwal_name || '',
            fontwal_designation: itemData.fontwal_designation || '',
            fontwal_date: itemData.fontwal_date || null,
            branch_head_name: itemData.branch_head_name || '',
            branch_head_designation: itemData.branch_head_designation || '',
            branch_head_date: itemData.branch_head_date || null,
            office_head_name: itemData.office_head_name || '',
            office_head_designation: itemData.office_head_designation || '',
            office_head_date: itemData.office_head_date || null,
            firm_name: itemData.firm_name || '',
            firm_date: itemData.firm_date || null,
            
            // Add items_data to the request body
            items_data: [itemDataForContext]
          };
          
          console.log(`DEBUG: Making Form403 POST request for item ${i + 1}/${data.length} with body:`, requestBody);
          console.log('DEBUG: Request URL:', `${API_BASE_URL}/api/form403/`);
          
          const form403Response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/form403/`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
          });
          
          console.log(`DEBUG: Form403 response status for item ${i + 1}:`, form403Response.status);
          console.log(`DEBUG: Form403 response ok for item ${i + 1}:`, form403Response.ok);
          
          if (!form403Response.ok) {
            const errorText = await form403Response.text();
            console.error(`Form403 creation error response for item ${i + 1}:`, errorText);
            console.error(`Form403 creation error status for item ${i + 1}:`, form403Response.status);
            console.error(`Form403 creation error headers for item ${i + 1}:`, Object.fromEntries(form403Response.headers.entries()));
            throw new Error(`Failed to create Form403 for item ${i + 1}: ${form403Response.status} - ${errorText}`);
          }
          
          const form403Result = await form403Response.json();
          
          // Create verification data for the created item
          if (form403Result.items && form403Result.items.length > 0) {
            const itemId = form403Result.items[0].id;
            const verificationResponse = await makeAuthenticatedRequest(`${API_BASE_URL}/api/form403-item-verification/`, {
              method: 'POST',
              body: JSON.stringify({
                form403_item: itemId,
                status: itemData.status,
                comment: itemData.comment,
                location: itemData.location
              })
            });
            
            if (!verificationResponse.ok) {
              console.warn(`Failed to create verification data for item ${itemId}`);
            }
          }
          
          results.push({ 
            success: true, 
            message: `Successfully created Form403 and item ${i + 1}/${data.length}: ${itemData.item_name || 'Unknown item'}` 
          });
          
          // Add a small delay between creations to ensure proper timestamp ordering
          // This ensures that each item gets a slightly different timestamp
          if (i < data.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
          }
          
        } catch (error) {
          console.error(`Error processing item ${i + 1}/${data.length}:`, error);
          results.push({ 
            success: false, 
            message: `Failed to process item ${i + 1}/${data.length}: ${itemData.item_name || 'Unknown'} - ${error.message}` 
          });
        }
      }
      
      // Count successes and failures
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;
      
      // Reset upload progress
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      
      // Show results
      const message = `Bulk create completed!\nSuccessfully created: ${successCount} forms with items\nFailed: ${errorCount} items\n\nNote: New items have been added at the top of the data list in the exact order they appeared in your Excel file.`;
      alert(message);
      
      // Refresh the data
      window.location.reload();
    } catch (error) {
      console.error('Error in bulk create:', error);
      
      // Reset upload progress on error
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      
      alert('Error during bulk create. Please try again.');
    }
  };

  // Get filtered rows
  const filteredRows = getFilteredVerificationRows();
  const paginatedRows = filteredRows.slice(
    (verificationCurrentPage - 1) * verificationItemsPerPage,
    verificationCurrentPage * verificationItemsPerPage
  );
  
  return (
    <div className={styles.dataVerificationContainer}>
      <h2>डाटा सत्यापन</h2>
      
      {/* Upload Progress Indicator */}
      {isUploading && (
        <div className={styles.uploadProgressContainer}>
          <div className={styles.progressHeader}>
            <span className={styles.progressIcon}>
              {spinningIcon === 1 ? '1' : spinningIcon === 2 ? '2' : spinningIcon === 3 ? '3' : '4'}
            </span>
            <span className={styles.progressTitle}>Uploading Excel Data...</span>
          </div>
          <div className={styles.progressBarContainer}>
            <div 
              className={styles.progressBar} 
              style={{ width: `${uploadProgress}%` }}
            ></div>
            <span className={styles.progressText}>{uploadProgress}%</span>
          </div>
          <div className={styles.progressStatus}>{uploadStatus}</div>
        </div>
      )}
      
      {/* Search and Controls */}
      <div className={styles.searchContainer}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="फारम खोज्नुहोस्..."
            value={verificationSearchTerm}
            onChange={handleVerificationSearch}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.itemsPerPageContainer}>
          <label>प्रति पृष्ठ:</label>
          <select
            value={verificationItemsPerPage}
            onChange={handleVerificationItemsPerPageChange}
            className={styles.itemsPerPageSelect}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={20000}>20000</option>
          </select>
        </div>
        
        {/* Filter Toggle Button */}
        <button
          onClick={toggleVerificationFilters}
          className={styles.filterToggleButton}
        >
          {showVerificationFilters ? 'फिल्टर लुकाउनुहोस्' : 'फिल्टर देखाउनुहोस्'}
        </button>
        
        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <button
            onClick={() => window.location.reload()}
            className={styles.actionButton}
            title="Refresh Data"
          >
            🔄 Refresh
          </button>
          
          {/* Download Template Button */}
          <button
            onClick={() => downloadTemplate()}
            className={styles.actionButton}
            title="Download Template"
          >
            📋 Download Template
          </button>
          
          {/* Order Structure Note */}
          <div className={styles.orderNote}>
            <span className={styles.orderIcon}>📋</span>
            <span className={styles.orderText}>
              <strong>Order Structure:</strong> New uploads are added at the top to maintain data order
            </span>
          </div>
          
          {/* Export Current Data Button */}
          <button
            onClick={() => exportCurrentData()}
            className={styles.actionButton}
            title="Export Current Data"
          >
            📥 Export Current Data
          </button>
          
                     {/* Upload Excel Button */}
           <button
             onClick={() => document.getElementById('excelUpload').click()}
             className={styles.actionButton}
             title="Bulk Create Forms from Excel Template"
           >
             📤 Bulk Create Forms
           </button>
          <input
            id="excelUpload"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelUpload}
            style={{ display: 'none' }}
          />
          
          {/* Excel Upload Instructions */}
          <div className={styles.uploadInstructions}>
            <div className={styles.instructionHeader}>
              <span className={styles.instructionIcon}>📋</span>
              <span className={styles.instructionTitle}>Excel Upload Instructions:</span>
            </div>
            <div className={styles.instructionSteps}>
              <div className={styles.instructionStep}>
                <span className={styles.stepNumber}>1.</span>
                <span className={styles.stepText}>Download the template using "Download Template" button</span>
              </div>
              <div className={styles.instructionStep}>
                <span className={styles.stepNumber}>2.</span>
                <span className={styles.stepText}>Fill in your data in the template (do not modify headers)</span>
              </div>
              <div className={styles.instructionStep}>
                <span className={styles.stepNumber}>3.</span>
                <span className={styles.stepText}>Save the file as .xlsx format</span>
              </div>
              <div className={styles.instructionStep}>
                <span className={styles.stepNumber}>4.</span>
                <span className={styles.stepText}>Upload using "Bulk Create Forms" button</span>
              </div>
              <div className={styles.instructionStep}>
                <span className={styles.stepNumber}>5.</span>
                <span className={styles.stepText}>New data will appear at the top in the exact order from your Excel file</span>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              // Get filtered rows for print
              const printFilteredRows = getFilteredVerificationRows();
              const printPaginatedRows = printFilteredRows.slice(
                (verificationCurrentPage - 1) * verificationItemsPerPage,
                verificationCurrentPage * verificationItemsPerPage
              );
              
              // Create print-friendly content with same styling as Excel
              const printContent = `
                <html>
                  <head>
                    <meta charset="utf-8">
                    <title>सामानहरूको सत्यापन सारांश</title>
                    <style>
                      body {
                        font-family: 'Noto Sans Devanagari', Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                        background: white;
                      }
                      table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                        font-size: 11px;
                      }
                      th, td {
                        border: 1px solid #000;
                        padding: 6px;
                        text-align: center;
                        vertical-align: middle;
                      }
                      th {
                        background-color: #2c3e50;
                        color: white;
                        font-weight: bold;
                      }
                      th.sub-header {
                        background-color: #34495e;
                        color: white;
                        font-weight: bold;
                      }
                       tr:nth-child(even) {
                         background-color: #f8f9fa;
                       }
                       tr:nth-child(odd) {
                         background-color: #ffffff;
                       }
                       .status-approved {
                         color: #28a745;
                         font-weight: bold;
                       }
                       .status-pending {
                         color: #ffc107;
                         font-weight: bold;
                       }
                       .title-row {
                         background-color: #f5f7fa;
                         border: 2px solid #2c3e50;
                         border-radius: 8px;
                         padding: 10px;
                         text-align: center;
                         margin-bottom: 20px;
                       }
                       .title-row h1 {
                         margin: 0;
                         color: #2c3e50;
                         font-size: 18px;
                         font-weight: bold;
                       }
                       @media print {
                         body { 
                           font-family: 'Noto Sans Devanagari', Arial, sans-serif; 
                           margin: 0; 
                           padding: 20px;
                           background: white;
                         }
                         table { 
                           width: 100%; 
                           border-collapse: collapse; 
                           margin: 20px 0; 
                           font-size: 11px;
                           page-break-inside: auto;
                         }
                         th, td { 
                           border: 1px solid #000; 
                           padding: 6px; 
                           text-align: center; 
                           vertical-align: middle;
                           page-break-inside: avoid;
                         }
                         th { 
                           background-color: #2c3e50 !important; 
                           color: white !important; 
                           font-weight: bold;
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         th.sub-header { 
                           background-color: #34495e !important; 
                           color: white !important; 
                           font-weight: bold;
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         tr:nth-child(even) { 
                           background-color: #f8f9fa !important; 
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         tr:nth-child(odd) { 
                           background-color: #ffffff !important; 
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         .status-approved { 
                           color: #28a745 !important; 
                           font-weight: bold; 
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         .status-pending { 
                           color: #ffc107 !important; 
                           font-weight: bold; 
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         .title-row {
                           background-color: #f5f7fa !important;
                           border: 2px solid #2c3e50 !important;
                           border-radius: 8px;
                           padding: 10px;
                           text-align: center;
                           margin-bottom: 20px;
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         .title-row h1 {
                           margin: 0;
                           color: #2c3e50 !important;
                           font-size: 18px;
                           font-weight: bold;
                           -webkit-print-color-adjust: exact;
                           print-color-adjust: exact;
                         }
                         @page {
                           size: A4 landscape;
                           margin: 1cm;
                         }
                       }
                     </style>
                   </head>
                   <body>
                     <div class="title-row">
                       <h1>सामानहरूको सत्यापन सारांश</h1>
                       <p>Form 403 - Data Verification Report</p>
                       <p>Generated on: ${new Date().toLocaleString('ne-NP')}</p>
                       <p>Total Items: ${printFilteredRows.length}</p>
                     </div>
                     <table>
                       <thead>
                         <tr>
                           <th rowspan="2">क्र.सं.</th>
                           <th rowspan="2">खरिद आदेश / हस्तान्तरण फारम नं.</th>
                           <th rowspan="2">जिन्सी वर्गीकरण संकेत नं.</th>
                           <th rowspan="2">जिन्सी खाता पाना नं.</th>
                           <th rowspan="2">सामानको नाम</th>
                           <th rowspan="2">स्पेसिफिकेशन</th>
                           <th rowspan="2">सामानको पहिचान नं.</th>
                           <th rowspan="2">मोडल नं.</th>
                           <th colspan="6">मूल्य (बिल बिजक अनुसार)</th>
                           <th rowspan="2">अन्य खर्च</th>
                           <th rowspan="2">अन्य खर्च समेत जम्मा रकम</th>
                           <th rowspan="2">कैफियत</th>
                           <th rowspan="2">स्थिति</th>
                           <th rowspan="2">टिप्पणी</th>
                           <th rowspan="2">स्थान</th>
                         </tr>
                         <tr>
                           <th class="sub-header">इकाई</th>
                           <th class="sub-header">परिमाण</th>
                           <th class="sub-header">दर</th>
                           <th class="sub-header">जम्मा मु.अ.कर बाहेक</th>
                           <th class="sub-header">मु.अ.कर</th>
                           <th class="sub-header">सामानको जम्मा मूल्य</th>
                         </tr>
                       </thead>
                       <tbody>
                         ${printPaginatedRows.map((form, index) => {
                           const status = verificationData[form.id]?.status === 'approved' ? 'स्वीकृत' : 'पेन्डिङ';
                           const statusClass = verificationData[form.id]?.status === 'approved' ? 'status-approved' : 'status-pending';
                           return `
                             <tr>
                               <td>${((verificationCurrentPage - 1) * verificationItemsPerPage) + index + 1}</td>
                               <td>${form.purchase_order_number || 'N/A'}</td>
                               <td>${form.classification_code || 'N/A'}</td>
                               <td>${form.ledger_page_number || 'N/A'}</td>
                               <td>${form.item_name || 'N/A'}</td>
                               <td>${form.specification || 'N/A'}</td>
                               <td>${form.item_identification_number || 'N/A'}</td>
                               <td>${form.model_number || 'N/A'}</td>
                               <td>${form.unit || 'N/A'}</td>
                               <td>${form.quantity || 'N/A'}</td>
                               <td>${form.rate || 'N/A'}</td>
                               <td>${form.total_excluding_vat || 'N/A'}</td>
                               <td>${form.vat || 'N/A'}</td>
                               <td>${form.total_item_value || 'N/A'}</td>
                               <td>${form.other_expenses || 'N/A'}</td>
                               <td>${form.total_with_other_expenses || 'N/A'}</td>
                               <td>${form.remarks || 'N/A'}</td>
                               <td class="${statusClass}">${status}</td>
                               <td>${verificationData[form.id]?.comment || ''}</td>
                               <td>${verificationData[form.id]?.location || ''}</td>
                             </tr>
                           `;
                         }).join('')}
                       </tbody>
                     </table>
                   </body>
                 </html>
               `;

                               // Create a new window for printing
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  printWindow.document.write(printContent);
                  printWindow.document.close();
                  
                  // Use setTimeout to ensure content is loaded
                  setTimeout(() => {
                    try {
                      printWindow.focus(); // Focus the window
                      printWindow.print();
                      // Close window after a short delay
                      setTimeout(() => {
                        printWindow.close();
                      }, 1000);
                    } catch (error) {
                      console.error('Print error:', error);
                      alert('Print failed. Please try again.');
                      printWindow.close();
                    }
                  }, 500);
                } else {
                  alert('Popup blocked! Please allow popups for this site and try again.');
                }
             }}
            className={styles.actionButton}
            title="Print Report"
          >
            🖨️ Print Report
          </button>
          <button
            onClick={() => exportToExcel()}
            className={styles.actionButton}
            title="Export to Excel"
          >
            📊 Export Excel
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showVerificationFilters && (
        <div className={styles.filterContainer}>
          <h3>फिल्टरहरू</h3>
          
          {/* Top-level form filters */}
          <div className={styles.filterSection}>
            <h4>फारम स्तर फिल्टरहरू</h4>
            <div className={styles.filterGrid}>
              <div className={styles.filterItem}>
                <label>दाखिला मिति:</label>
                <input
                  type="text"
                  value={verificationFilters.admissionDate || ''}
                  onChange={(e) => handleVerificationFilterChange('admission_date', e.target.value)}
                  placeholder="दाखिला मिति..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>दाखिला प्रतिवेदन नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.admissionReportNumber || ''}
                  onChange={(e) => handleVerificationFilterChange('admission_report_number', e.target.value)}
                  placeholder="दाखिला प्रतिवेदन नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>कार्यालय कोड नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.officeCode || ''}
                  onChange={(e) => handleVerificationFilterChange('office_code', e.target.value)}
                  placeholder="कार्यालय कोड नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>कार्यालय:</label>
                <input
                  type="text"
                  value={verificationFilters.officeName || ''}
                  onChange={(e) => handleVerificationFilterChange('office_name', e.target.value)}
                  placeholder="कार्यालय..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>कार्यालय ठेगाना:</label>
                <input
                  type="text"
                  value={verificationFilters.officeAddress || ''}
                  onChange={(e) => handleVerificationFilterChange('office_address', e.target.value)}
                  placeholder="कार्यालय ठेगाना..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>फाँटवालाको नाम:</label>
                <input
                  type="text"
                  value={verificationFilters.fontwalName || ''}
                  onChange={(e) => handleVerificationFilterChange('fontwal_name', e.target.value)}
                  placeholder="फाँटवालाको नाम..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>भण्डार प्रमुखको नाम:</label>
                <input
                  type="text"
                  value={verificationFilters.branchHeadName || ''}
                  onChange={(e) => handleVerificationFilterChange('branch_head_name', e.target.value)}
                  placeholder="भण्डार प्रमुखको नाम..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>प्रमाणित गर्नेको नाम:</label>
                <input
                  type="text"
                  value={verificationFilters.officeHeadName || ''}
                  onChange={(e) => handleVerificationFilterChange('office_head_name', e.target.value)}
                  placeholder="प्रमाणित गर्नेको नाम..."
                  className={styles.filterInput}
                />
              </div>
            </div>
          </div>
          
          {/* Item-level filters */}
          <div className={styles.filterSection}>
            <h4>सामान स्तर फिल्टरहरू</h4>
            <div className={styles.filterGrid}>
              <div className={styles.filterItem}>
                <label>खरिद आदेश / हस्तान्तरण फारम नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.purchaseOrderNumber || ''}
                  onChange={(e) => handleVerificationFilterChange('purchase_order_number', e.target.value)}
                  placeholder="खरिद आदेश नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>जिन्सी वर्गीकरण संकेत नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.classificationCode || ''}
                  onChange={(e) => handleVerificationFilterChange('classification_code', e.target.value)}
                  placeholder="वर्गीकरण संकेत नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>जिन्सी खाता पाना नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.ledgerPageNumber || ''}
                  onChange={(e) => handleVerificationFilterChange('ledger_page_number', e.target.value)}
                  placeholder="खाता पाना नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>सामानको नाम:</label>
                <input
                  type="text"
                  value={verificationFilters.itemName || ''}
                  onChange={(e) => handleVerificationFilterChange('item_name', e.target.value)}
                  placeholder="सामानको नाम..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>स्पेसिफिकेशन:</label>
                <input
                  type="text"
                  value={verificationFilters.specification || ''}
                  onChange={(e) => handleVerificationFilterChange('specification', e.target.value)}
                  placeholder="स्पेसिफिकेशन..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>सामानको पहिचान नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.itemIdentificationNumber || ''}
                  onChange={(e) => handleVerificationFilterChange('item_identification_number', e.target.value)}
                  placeholder="पहिचान नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>मोडल नं.:</label>
                <input
                  type="text"
                  value={verificationFilters.modelNumber || ''}
                  onChange={(e) => handleVerificationFilterChange('model_number', e.target.value)}
                  placeholder="मोडल नं..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>इकाई:</label>
                <input
                  type="text"
                  value={verificationFilters.unit || ''}
                  onChange={(e) => handleVerificationFilterChange('unit', e.target.value)}
                  placeholder="इकाई..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>परिमाण:</label>
                <input
                  type="text"
                  value={verificationFilters.quantity || ''}
                  onChange={(e) => handleVerificationFilterChange('quantity', e.target.value)}
                  placeholder="परिमाण..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>दर:</label>
                <input
                  type="text"
                  value={verificationFilters.rate || ''}
                  onChange={(e) => handleVerificationFilterChange('rate', e.target.value)}
                  placeholder="दर..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>जम्मा मु.अ.कर बाहेक:</label>
                <input
                  type="text"
                  value={verificationFilters.totalExcludingVAT || ''}
                  onChange={(e) => handleVerificationFilterChange('total_excluding_vat', e.target.value)}
                  placeholder="जम्मा मु.अ.कर बाहेक..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>मु.अ.कर:</label>
                <input
                  type="text"
                  value={verificationFilters.vat || ''}
                  onChange={(e) => handleVerificationFilterChange('vat', e.target.value)}
                  placeholder="मु.अ.कर..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>सामानको जम्मा मूल्य:</label>
                <input
                  type="text"
                  value={verificationFilters.totalItemValue || ''}
                  onChange={(e) => handleVerificationFilterChange('total_item_value', e.target.value)}
                  placeholder="जम्मा मूल्य..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>अन्य खर्च:</label>
                <input
                  type="text"
                  value={verificationFilters.otherExpenses || ''}
                  onChange={(e) => handleVerificationFilterChange('other_expenses', e.target.value)}
                  placeholder="अन्य खर्च..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>अन्य खर्च समेत जम्मा रकम:</label>
                <input
                  type="text"
                  value={verificationFilters.totalWithOtherExpenses || ''}
                  onChange={(e) => handleVerificationFilterChange('total_with_other_expenses', e.target.value)}
                  placeholder="जम्मा रकम..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>कैफियत:</label>
                <input
                  type="text"
                  value={verificationFilters.remarks || ''}
                  onChange={(e) => handleVerificationFilterChange('remarks', e.target.value)}
                  placeholder="कैफियत..."
                  className={styles.filterInput}
                />
              </div>
            </div>
          </div>
          
          {/* Verification-specific filters */}
          <div className={styles.filterSection}>
            <h4>सत्यापन फिल्टरहरू</h4>
            <div className={styles.filterGrid}>
              <div className={styles.filterItem}>
                <label>स्थिति:</label>
                <select
                  value={verificationFilters.status || ''}
                  onChange={(e) => handleVerificationFilterChange('status', e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">सबै</option>
                  <option value="pending">पेन्डिङ</option>
                  <option value="approved">स्वीकृत</option>
                  <option value="rejected">अस्वीकृत</option>
                  <option value="under_review">समीक्षामा</option>
                </select>
              </div>
              
              <div className={styles.filterItem}>
                <label>टिप्पणी:</label>
                <input
                  type="text"
                  value={verificationFilters.comment || ''}
                  onChange={(e) => handleVerificationFilterChange('comment', e.target.value)}
                  placeholder="टिप्पणी..."
                  className={styles.filterInput}
                />
              </div>
              
              <div className={styles.filterItem}>
                <label>स्थान:</label>
                <input
                  type="text"
                  value={verificationFilters.location || ''}
                  onChange={(e) => handleVerificationFilterChange('location', e.target.value)}
                  placeholder="स्थान..."
                  className={styles.filterInput}
                />
              </div>
            </div>
          </div>
          
          {/* Filter Results Info */}
          <div className={styles.filterInfo}>
            <span>फिल्टर गरिएका परिणामहरू: {filteredRows.length} / {verificationFormList.length}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {verificationLoading ? (
        <div className={styles.loadingContainer}>
          <p>फारमहरू लोड गर्दै...</p>
        </div>
      ) : (
        <>
          {/* Verification Forms Table */}
          <div className={styles.tableContainer}>
            <h3>सामानहरूको सत्यापन सारांश</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th rowSpan="2">क्र.सं.</th>
                  <th rowSpan="2">खरिद आदेश / हस्तान्तरण फारम नं.</th>
                  <th rowSpan="2">जिन्सी वर्गीकरण संकेत नं.</th>
                  <th rowSpan="2">जिन्सी खाता पाना नं.</th>
                  <th rowSpan="2">सामानको नाम</th>
                  <th rowSpan="2">स्पेसिफिकेशन</th>
                  <th rowSpan="2">सामानको पहिचान नं.</th>
                  <th rowSpan="2">मोडल नं.</th>
                  <th colSpan="6">मूल्य (बिल बिजक अनुसार)</th>
                  <th rowSpan="2">अन्य खर्च</th>
                  <th rowSpan="2">अन्य खर्च समेत जम्मा रकम</th>
                  <th rowSpan="2">कैफियत</th>
                  <th rowSpan="2">स्थिति</th>
                  <th rowSpan="2">टिप्पणी</th>
                  <th rowSpan="2">स्थान</th>
                </tr>
                <tr>
                  <th>इकाई</th>
                  <th>परिमाण</th>
                  <th>दर</th>
                  <th>जम्मा मु.अ.कर बाहेक</th>
                  <th>मु.अ.कर</th>
                  <th>सामानको जम्मा मूल्य</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((form, index) => {
                  // Debug logging for each form
                  console.log(`Rendering form ${form.id}:`, {
                    formId: form.id,
                    itemName: form.item_name,
                    hasVerificationData: !!verificationData[form.id],
                    verificationData: verificationData[form.id]
                  });
                  
                  return (
                    <tr key={form.id}>
                      <td>{((verificationCurrentPage - 1) * verificationItemsPerPage) + index + 1}</td>
                      <td>{form.purchase_order_number || 'N/A'}</td>
                      <td>{form.classification_code || 'N/A'}</td>
                      <td>{form.ledger_page_number || 'N/A'}</td>
                      <td>{form.item_name || 'N/A'}</td>
                      <td>{form.specification || 'N/A'}</td>
                      <td>{form.item_identification_number || 'N/A'}</td>
                      <td>{form.model_number || 'N/A'}</td>
                      <td>{form.unit || 'N/A'}</td>
                      <td>{form.quantity || 'N/A'}</td>
                      <td>{form.rate || 'N/A'}</td>
                      <td>{form.total_excluding_vat || 'N/A'}</td>
                      <td>{form.vat || 'N/A'}</td>
                      <td>{form.total_item_value || 'N/A'}</td>
                      <td>{form.other_expenses || 'N/A'}</td>
                      <td>{form.total_with_other_expenses || 'N/A'}</td>
                      <td>{form.remarks || 'N/A'}</td>
                      <td>
                        <div className={styles.statusContainer}>
                          <input
                            type="checkbox"
                            checked={verificationData[form.id]?.status === 'approved' || false}
                            className={styles.statusCheckbox}
                            onChange={(e) => {
                              console.log(`Status change for form ${form.id}: ${e.target.checked}`);
                              handleVerificationStatusChange(form.id, e.target.checked);
                              // Auto-save on change
                              saveVerificationData(form.id, {
                                status: e.target.checked ? 'approved' : 'pending',
                                comment: verificationData[form.id]?.comment || '',
                                location: verificationData[form.id]?.location || ''
                              });
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={verificationData[form.id]?.comment || ''}
                          placeholder="टिप्पणी..."
                          className={styles.commentInput}
                          onChange={(e) => {
                            handleVerificationCommentChange(form.id, e.target.value);
                            // Auto-save on change with debounce
                            clearTimeout(window.commentSaveTimeout);
                            window.commentSaveTimeout = setTimeout(() => {
                              saveVerificationData(form.id, {
                                status: verificationData[form.id]?.status || 'pending',
                                comment: e.target.value,
                                location: verificationData[form.id]?.location || ''
                              });
                            }, 500);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={verificationData[form.id]?.location || ''}
                          placeholder="स्थान..."
                          className={styles.locationInput}
                          onChange={(e) => {
                            handleVerificationLocationChange(form.id, e.target.value);
                            // Auto-save on change with debounce
                            clearTimeout(window.locationSaveTimeout);
                            window.locationSaveTimeout = setTimeout(() => {
                              saveVerificationData(form.id, {
                                status: verificationData[form.id]?.status || 'pending',
                                comment: verificationData[form.id]?.comment || '',
                                location: e.target.value
                              });
                            }, 500);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {Math.ceil(filteredRows.length / verificationItemsPerPage) > 1 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>पृष्ठ {verificationCurrentPage} को {Math.ceil(filteredRows.length / verificationItemsPerPage)}</span>
              </div>
              
              <div className={styles.paginationButtons}>
                <button
                  onClick={() => handleVerificationPageChange(1)}
                  disabled={verificationCurrentPage === 1}
                  className={styles.paginationButton}
                >
                  पहिलो
                </button>
                <button
                  onClick={() => handleVerificationPageChange(verificationCurrentPage - 1)}
                  disabled={verificationCurrentPage === 1}
                  className={styles.paginationButton}
                >
                  अघिल्लो
                </button>
                
                {Array.from({ length: Math.min(5, Math.ceil(filteredRows.length / verificationItemsPerPage)) }, (_, i) => {
                  const page = Math.max(1, Math.min(Math.ceil(filteredRows.length / verificationItemsPerPage) - 4, verificationCurrentPage - 2)) + i;
                  return (
                    <button
                      key={page}
                      onClick={() => handleVerificationPageChange(page)}
                      className={`${styles.paginationButton} ${verificationCurrentPage === page ? styles.activePage : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handleVerificationPageChange(verificationCurrentPage + 1)}
                  disabled={verificationCurrentPage === Math.ceil(filteredRows.length / verificationItemsPerPage)}
                  className={styles.paginationButton}
                >
                  अर्को
                </button>
                <button
                  onClick={() => handleVerificationPageChange(Math.ceil(filteredRows.length / verificationItemsPerPage))}
                  disabled={verificationCurrentPage === Math.ceil(filteredRows.length / verificationItemsPerPage)}
                  className={styles.paginationButton}
                >
                  अन्तिम
                </button>
              </div>
            </div>
          )}

          {/* No Data Message */}
          {filteredRows.length === 0 && !verificationLoading && (
            <div className={styles.noDataContainer}>
              <p>कुनै फारमहरू फेला परेनन्।</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VerifyData;
